import type { CoreConversationWithMessagesModel } from "../../api/api";
import { HTTP_HEADER, HTTP_STATUS } from "../../api/HttpProtocol";
import { isAgentProgress } from "../Components/Chat/chatProgressPolicy";

export const CHAT_POLLING = { activeMs: 1500, idleMs: 15000, maximumIdleMs: 30000, maximumBackoffMs: 60000, maximumActiveMs: 300000 } as const;

const CHAT_PARTICIPANT_TYPE = { agent: "Agent", user: "User" } as const;
const BROWSER_EVENT = { visibilityChange: "visibilitychange", online: "online", offline: "offline" } as const;
const HIDDEN_VISIBILITY_STATE: DocumentVisibilityState = "hidden";
const millisecondsPerSecond = 1000;
const maximumTimerDelayMs = 2147483647;
const jitterRatio = 0.1;
const backoffMultiplier = 2;
const maximumBackoffExponent = 6;

/** Activity budget is independent of the short-lived visual typing indicator. */
export function chatActivityStartedAt(conversation?: CoreConversationWithMessagesModel, sentAt = 0): number {
  if (conversation?.responseStatus === "queued" || conversation?.responseStatus === "responding") {
    const started = Date.parse(conversation.responseUpdatedAt ?? "");
    if (Number.isFinite(started)) return started;
  }
  const agents = new Set(conversation?.participants?.filter((p) => p.type === CHAT_PARTICIPANT_TYPE.agent).map((p) => p.id));
  if (!agents.size) return sentAt;
  const messages = conversation?.messages ?? [];
  const dates = (values: (string | undefined)[]) => values.map((value) => Date.parse(value ?? "")).filter(Number.isFinite);
  const reply = Math.max(0, ...dates(messages.filter((m) => agents.has(m.participantId) && !isAgentProgress(m)).map((m) => m.createdDate)));
  const users = new Set(conversation?.participants?.filter((p) => p.type === CHAT_PARTICIPANT_TYPE.user).map((p) => p.id));
  const starts = [
    sentAt,
    ...dates(messages.filter((m) => users.has(m.participantId) || (agents.has(m.participantId) && isAgentProgress(m))).map((m) => m.createdDate)),
    ...dates(conversation?.participants?.filter((p) => agents.has(p.id)).map((p) => p.lastMessageStarted) ?? []),
  ];
  return Math.max(0, ...starts.filter((start) => start > reply));
}

type PollingFailure = { status?: number; headers?: Record<string, string>; detail?: { status?: number; retryAfter?: string | null } };
export function pollingFailure(error: unknown) {
  const failure = (error && typeof error === "object" ? error : {}) as PollingFailure;
  const status = failure.status ?? failure.detail?.status;
  const header =
    Object.entries(failure.headers ?? {}).find(([name]) => name.toLowerCase() === HTTP_HEADER.retryAfter.toLowerCase())?.[1] ?? failure.detail?.retryAfter;
  const seconds = header ? Number(header) : NaN;
  const retryAt = header ? (Number.isFinite(seconds) && seconds >= 0 ? Date.now() + seconds * millisecondsPerSecond : Date.parse(header)) : 0;
  return {
    terminal: status === HTTP_STATUS.unauthorized || status === HTTP_STATUS.forbidden || status === HTTP_STATUS.notFound,
    retryAt: Number.isFinite(retryAt) ? retryAt : 0,
  };
}

/** One timer and one read, with visibility/reconnect wakeups and rate-limit-aware retries. */
export class ChatPollingScheduler {
  private timer?: ReturnType<typeof setTimeout>;
  private disposed = false;
  private running = false;
  private wakePending = false;
  private terminal = false;
  private failures = 0;
  private idlePolls = 0;
  private notBefore = 0;
  constructor(
    private readonly refresh: () => Promise<void>,
    private readonly activity: () => number,
    private readonly random = Math.random
  ) {}
  private available = () => document.visibilityState !== HIDDEN_VISIBILITY_STATE && navigator.onLine !== false;
  start(immediate = false) {
    document.addEventListener(BROWSER_EVENT.visibilityChange, this.wake);
    window.addEventListener(BROWSER_EVENT.online, this.wake);
    window.addEventListener(BROWSER_EVENT.offline, this.pause);
    if (immediate && this.available()) void this.poll();
    else this.schedule(CHAT_POLLING.activeMs);
  }
  private pause = () => {
    clearTimeout(this.timer);
  };
  private wake = () => {
    this.pause();
    if (this.running) this.wakePending = true;
    else this.schedule(0);
  };
  notifyActivity = () => {
    this.idlePolls = 0;
    if (!this.running) {
      this.pause();
      this.schedule(CHAT_POLLING.activeMs);
    }
  };
  resume = () => {
    this.terminal = false;
    this.notifyActivity();
  };
  private schedule(delay: number) {
    if (this.disposed || this.terminal || !this.available()) return;
    const remaining = Math.max(delay, this.notBefore - Date.now(), 0);
    this.timer = setTimeout(this.poll, Math.min(remaining, maximumTimerDelayMs));
  }
  private jitter(delay: number) {
    return Math.round(delay * (1 - jitterRatio + this.random() * jitterRatio * 2));
  }
  private poll = async () => {
    if (this.disposed || this.terminal || !this.available() || this.running) return;
    if (Date.now() < this.notBefore) {
      this.schedule(0);
      return;
    }
    this.running = true;
    let delay: number;
    try {
      await this.refresh();
      this.failures = 0;
      this.notBefore = 0;
      const started = this.activity();
      const active = started > 0 && started <= Date.now() && Date.now() - started < CHAT_POLLING.maximumActiveMs;
      this.idlePolls = active ? 0 : this.idlePolls + 1;
      delay = this.jitter(active ? CHAT_POLLING.activeMs : Math.min(CHAT_POLLING.maximumIdleMs, CHAT_POLLING.idleMs * this.idlePolls));
    } catch (error) {
      const failure = pollingFailure(error);
      this.terminal = failure.terminal;
      this.failures++;
      delay = this.jitter(
        Math.min(CHAT_POLLING.maximumBackoffMs, CHAT_POLLING.activeMs * backoffMultiplier ** Math.min(this.failures, maximumBackoffExponent))
      );
      this.notBefore = Math.max(Date.now() + delay, failure.retryAt);
    } finally {
      this.running = false;
      const wake = this.wakePending;
      this.wakePending = false;
      this.schedule(wake ? 0 : delay!);
    }
  };
  stop() {
    this.disposed = true;
    this.pause();
    document.removeEventListener(BROWSER_EVENT.visibilityChange, this.wake);
    window.removeEventListener(BROWSER_EVENT.online, this.wake);
    window.removeEventListener(BROWSER_EVENT.offline, this.pause);
  }
}
